import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { TbCheck, TbChevronDown } from 'react-icons/tb';
import { ISelectOption } from 'types';
import { wrap, trigger, label, arrow, menu, item, check } from './style';

interface Props<T extends string> {
  options: ISelectOption<T>[];
  value: T[];
  handleChange: (value: T[]) => void;
  title?: string;
  allLabel?: string;
  css?: Interpolation<Theme>;
}

function MultiSelect<T extends string>({
  options,
  value,
  handleChange,
  title,
  allLabel = '전체',
  css,
}: Props<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const isAll = value.length === 0 || value.length === options.length;

  const summary = useMemo(() => {
    if (isAll) return allLabel;
    const names = options
      .filter((o) => value.includes(o.value))
      .map((o) => o.name);
    return names.length > 1 ? `${names[0]} 외 ${names.length - 1}` : names[0];
  }, [isAll, options, value, allLabel]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const handleKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && setIsOpen(false);
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const toggle = (v: T) =>
    handleChange(
      value.includes(v) ? value.filter((d) => d !== v) : [...value, v],
    );

  const toggleAll = () =>
    handleChange(
      value.length === options.length ? [] : options.map((o) => o.value),
    );

  return (
    <div css={[wrap, css]} ref={ref}>
      <button
        type="button"
        css={trigger(isOpen, !isAll)}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {title && <span css={label}>{title}</span>}
        <span>{summary}</span>
        <TbChevronDown size={16} css={arrow(isOpen)} />
      </button>
      {isOpen && (
        <ul css={menu} role="listbox" aria-multiselectable>
          <li
            css={item}
            role="option"
            aria-selected={value.length === options.length}
            onClick={toggleAll}
          >
            <span css={check(value.length === options.length)}>
              <TbCheck size={14} />
            </span>
            {allLabel}
          </li>
          {options.map((o) => (
            <li
              key={o.value}
              css={item}
              role="option"
              aria-selected={value.includes(o.value)}
              aria-disabled={o.disabled}
              onClick={() => !o.disabled && toggle(o.value)}
            >
              <span css={check(value.includes(o.value))}>
                <TbCheck size={14} />
              </span>
              {o.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default MultiSelect;
